import {
  calculateMonthlyBandwidthUsagePercentage,
  canBuyCredits,
  creditTypeConfig,
  getCreditBreakdown,
  isFrictionlessSignupUser,
} from '@frontend/api-client';
import type { Credit } from '@frontend/api-types';
import { gradients } from '@frontend/constants';
import { capitalize, convertDateLikeToDateTimeISOString, dateFormatters, dayjs } from '@frontend/duck-tape';
import { useFeatureValue } from '@frontend/react';
import { Button, Icon, Text, XStack, YStack } from '@frontend/web-react';
import { usePricingTerminology } from '@frontend/web/context';
import {
  useRetrieveClientQuery,
  useRetrieveClientStatusQuery,
  useRetrieveFullLedgerReportQuery,
} from '@frontend/web/hooks';
import { useMemo, useState } from 'react';
import { BuyCreditsModal } from '../Payment/BuyCreditsModal';
import { CreditHistoryModal } from './CreditHistoryModal';

type MonthlyBandwidthProps = {
  credits?: Credit;
  onCreditsRefetch?: () => void;
};

const timeframe = {
  end: convertDateLikeToDateTimeISOString(dayjs().startOf('minute').toISOString()),
  start: convertDateLikeToDateTimeISOString(dayjs().startOf('minute').subtract(2, 'month').toISOString()),
};

export const MonthlyBandwidth = ({ credits, onCreditsRefetch }: MonthlyBandwidthProps) => {
  const { data: client } = useRetrieveClientQuery();
  const { data: clientStatus } = useRetrieveClientStatusQuery();
  const { pricingTerminology, showCredits } = usePricingTerminology();
  const [isExpanded, setIsExpanded] = useState(false);
  const [modalKey, setModalKey] = useState<'buy' | 'history'>();
  const closeModal = () => setModalKey(undefined);
  const openModal = (modal: 'buy' | 'history') => setModalKey(modal);

  const {
    data: ledgerEntries = [],
    isError: ledgerError,
    isLoading: loadingLedger,
    refetch: refetchLedger,
  } = useRetrieveFullLedgerReportQuery({
    end: timeframe.end,
    start: timeframe.start,
  });

  const { value: progressDirection } = useFeatureValue('monthly_bandwidth_progress_direction', 'drain');
  const { value: trialCredits } = useFeatureValue('experiment_no_payment_signup_credits', 0);

  const trialMaxCredits = isFrictionlessSignupUser(client) ? trialCredits : undefined;
  const usagePercentage = calculateMonthlyBandwidthUsagePercentage(credits, progressDirection, trialMaxCredits);
  const isLowBandwidth = usagePercentage <= 10;
  const progressGradient = isLowBandwidth ? gradients.progressBarLow : gradients.progressBar;
  const renewalDate = credits?.subscriptionRenewalDate;

  const creditBreakdown = useMemo(
    () => getCreditBreakdown(credits?.activeAllocations ?? []),
    [credits?.activeAllocations],
  );

  return (
    <>
      <YStack gap="sm">
        <YStack bg="trueWhite" borderColor="borderPrimary" borderWidth={1} gap="md" p="md" radius="lg">
          <XStack alignItems="center" gap="xs">
            <Text type="p-sm">{pricingTerminology.balance_title}</Text>
            <Icon name="IconInfoCircle" onClick={() => setIsExpanded(!isExpanded)} size={18} />
          </XStack>
          <YStack gap="sm">
            <XStack alignItems="baseline" justifyContent="between">
              <XStack alignItems="baseline" gap="xs">
                <Text type="h2">{Math.round(usagePercentage)}%</Text>
                <Text color="tertiary" type="p-sm">
                  remaining
                </Text>
              </XStack>
              <Text color="tertiary" type="p-sm">
                Renews {renewalDate ? dateFormatters['MMM D'](renewalDate) : ''}
              </Text>
            </XStack>
            <YStack bg="cream400" radius="sm" style={{ height: 16, width: '100%' }}>
              <YStack
                radius="sm"
                style={{
                  background: `linear-gradient(90deg, ${progressGradient.colors[0]} 0%, ${progressGradient.colors[1]} ${progressGradient.locations[1] * 100}%)`,
                  height: 16,
                  overflow: 'hidden',
                  width: `${usagePercentage}%`,
                }}
              />
            </YStack>
          </YStack>
          <XStack gap="sm" mt="xs">
            <XStack flexGrow={1} gap="sm">
              <Button
                label={pricingTerminology.history_title}
                onClick={() => openModal('history')}
                size="sm"
                variant="secondary"
              />
              {canBuyCredits({ client, clientStatus, credits }) && (
                <Button
                  label={pricingTerminology.add_button}
                  onClick={() => openModal('buy')}
                  size="sm"
                  variant="primary"
                />
              )}
            </XStack>
            <Button
              aria-label={isExpanded ? 'Hide details' : 'Show details'}
              onClick={() => setIsExpanded(!isExpanded)}
              size="sm"
              variant="secondary"
            >
              <Icon color="primary" name={isExpanded ? 'IconChevronUp' : 'IconChevronDown'} />
            </Button>
          </XStack>
          {isExpanded ? (
            showCredits ? (
              <YStack borderColor="borderSecondary" borderTop gap="md" pt="md">
                <Text color="secondary" type="p-md">
                  {`${capitalize(pricingTerminology.unit_term)} details`}
                </Text>
                <YStack>
                  {creditBreakdown.map((item, index) => {
                    const itemConfig = creditTypeConfig[item.creditType];
                    const isLastItem = index === creditBreakdown.length - 1;
                    return (
                      <XStack
                        alignItems="center"
                        borderBottom={!isLastItem}
                        borderColor={!isLastItem ? 'neutral200' : undefined}
                        justifyContent="between"
                        key={item.allocationId}
                        py="sm"
                      >
                        <XStack alignItems="center" flex={1} gap="md">
                          <Icon className="cursor-default" color="secondary" name={itemConfig?.icon} size={20} />
                          <Text type="p-md">{itemConfig?.label ?? item.creditType}</Text>
                        </XStack>
                        <XStack alignItems="center" gap="lg">
                          <Text type="p-md">{item.remainingCredits}</Text>
                          <Text color="secondary" style={{ minWidth: 100 }} textAlign="end" type="p-sm">
                            Expire {item.expiration ? dateFormatters['M/D'](item.expiration) : 'never'}
                          </Text>
                        </XStack>
                      </XStack>
                    );
                  })}
                </YStack>
              </YStack>
            ) : (
              <YStack bg="cream100" gap="xs" mt="xs" radius="md" style={{ padding: 12 }}>
                <Text type="p-md">{`How monthly ${pricingTerminology.unit_term} works`}</Text>
                <Text color="tertiary" type="p-sm">
                  {`Your plan includes a monthly allocation of ${pricingTerminology.unit_term} for you to use toward human work hours. You'll be able to see your progress here. You may click "${pricingTerminology.add_button}" to purchase additional ${pricingTerminology.unit_term} at any point. ${capitalize(pricingTerminology.unit_term)} refreshes each month.`}
                </Text>
              </YStack>
            )
          ) : null}
        </YStack>
      </YStack>
      <CreditHistoryModal
        closeModal={closeModal}
        isError={ledgerError}
        isLoading={loadingLedger}
        isOpen={modalKey === 'history'}
        ledgerEntries={ledgerEntries}
        pricingTerminology={pricingTerminology}
        subscriptionRenewalCredits={credits?.subscriptionRenewalCredits}
      />
      <BuyCreditsModal
        closeModal={closeModal}
        isOpen={modalKey === 'buy'}
        onCreditsPurchased={() => {
          onCreditsRefetch?.();
          refetchLedger();
        }}
      />
    </>
  );
};
